import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { BarChart3, Eye, Heart, MessageCircle, Share2, MousePointerClick, TrendingUp, ExternalLink, RefreshCw, PlugZap } from "lucide-react";
import {
  adminSocialOverview,
  adminSocialTimeSeries,
  socialSyncGhlMetrics,
} from "@/lib/social-analytics.functions";
import { SocialSubNav } from "@/components/admin/SocialSubNav";
import { TrendCharts } from "@/components/social/TrendCharts";

export const Route = createFileRoute("/admin/social/analytics")({
  head: () => ({ meta: [
    { title: "Social Analytics · Simply Approved Admin" },
    { name: "description", content: "Review provider-backed social performance metrics." },
    { property: "og:title", content: "Social Analytics · Simply Approved Admin" },
    { property: "og:description", content: "Review provider-backed social performance metrics." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
    { name: "robots", content: "noindex, nofollow" },
  ] }),
  component: AnalyticsPage,
});

const KPI_STYLES = [
  { bg: "bg-primary/5", ring: "ring-primary/20", text: "text-primary", icon: "text-primary" },
  { bg: "bg-rose-500/5", ring: "ring-rose-500/20", text: "text-rose-600", icon: "text-rose-500" },
  { bg: "bg-accent/10", ring: "ring-accent/30", text: "text-accent-foreground", icon: "text-accent" },
  { bg: "bg-emerald-500/5", ring: "ring-emerald-500/20", text: "text-emerald-600", icon: "text-emerald-500" },
  { bg: "bg-amber-500/5", ring: "ring-amber-500/20", text: "text-amber-600", icon: "text-amber-500" },
];

function Kpi({
  icon: Icon,
  label,
  value,
  tone,
  available,
}: {
  icon: typeof BarChart3;
  label: string;
  value: number;
  tone: number;
  available: boolean;
}) {
  const s = KPI_STYLES[tone % KPI_STYLES.length];
  return (
    <div className={`rounded-xl border border-border ${s.bg} ring-1 ${s.ring} p-4`}>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Icon className={`h-4 w-4 ${s.icon}`} />
        {label}
      </div>
      {available ? (
        <div className={`mt-1 text-3xl font-bold ${s.text}`}>{value.toLocaleString()}</div>
      ) : (
        <div className="mt-1">
          <div className={`text-3xl font-bold ${s.text} opacity-40`}>—</div>
          <div className="text-[10px] text-muted-foreground mt-0.5" title="GoHighLevel does not expose this metric for the connected accounts (hasStatisticsPermissions is false).">
            Not provided by GHL
          </div>
        </div>
      )}
    </div>
  );
}

function Bar({ value, max, tone = "bg-primary" }: { value: number; max: number; tone?: string }) {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
      <div className={`h-full ${tone}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function AnalyticsPage() {
  const fn = useServerFn(adminSocialOverview);
  const syncFn = useServerFn(socialSyncGhlMetrics);
  const trendFn = useServerFn(adminSocialTimeSeries);
  const qc = useQueryClient();
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  const [days, setDays] = useState<30 | 90>(30);

  const { data, isLoading } = useQuery({ queryKey: ["social-overview"], queryFn: () => fn() });
  const trend = useQuery({ queryKey: ["social-trend", days], queryFn: () => trendFn({ data: { days } }) });
  const sync = useMutation({
    mutationFn: () => syncFn(),
    onSuccess: (r: any) => {
      setSyncMsg(r?.ok
        ? `Synced ${r.synced} snapshot${r.synced === 1 ? "" : "s"} from ${r.total} GHL post${r.total === 1 ? "" : "s"}${r.mirrorsCreated ? ` · ${r.mirrorsCreated} new mirror row${r.mirrorsCreated === 1 ? "" : "s"}` : ""}${r.accountsConnected === 0 ? " · no connected GHL accounts detected" : ""}.`
        : (r?.error ?? "Sync failed"));
      qc.invalidateQueries({ queryKey: ["social-overview"] });
      qc.invalidateQueries({ queryKey: ["social-trend"] });
    },
    onError: (e: any) => setSyncMsg(e?.message ?? "Sync failed"),
  });

  const t = data?.totals ?? { impressions: 0, likes: 0, comments: 0, shares: 0, clicks: 0 };
  const platformAgg = data?.platformAgg ?? {};
  const topPosts = data?.topPosts ?? [];
  const maxImpressions = Math.max(1, ...Object.values(platformAgg).map((p: any) => p.impressions));
  const maxScore = Math.max(1, ...topPosts.map((p: any) => p.score));

  return (
    <div>
      <SocialSubNav />
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-primary flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-accent" /> Social analytics
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {isLoading
              ? "Loading…"
              : `${data?.authoredCount ?? data?.total ?? 0} authored · ${data?.mirrorCount ?? 0} GHL sync mirror (read-only).`}
          </p>
          {data?.lastSyncAt && (
            <p className="mt-0.5 text-xs text-muted-foreground">
              Last GHL sync: {new Date(data.lastSyncAt).toLocaleString()}
            </p>
          )}
          {syncMsg && <p className="mt-1 text-xs text-accent-foreground">{syncMsg}</p>}
        </div>
        <div className="flex gap-2">
          <Link
            to="/admin/social/connect-ghl"
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-sm font-medium hover:bg-muted"
          >
            <PlugZap className="h-4 w-4" /> Connect GHL
          </Link>
          <button
            onClick={() => { setSyncMsg(null); sync.mutate(); }}
            disabled={sync.isPending}
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-primary text-primary-foreground px-3 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${sync.isPending ? "animate-spin" : ""}`} />
            {sync.isPending ? "Syncing…" : "Sync from GHL"}
          </button>
        </div>
      </div>

      <div className="mt-6 grid gap-3 md:grid-cols-5">
        <Kpi icon={Eye} label="Impressions" value={t.impressions} tone={0} available={data?.capability?.impressions ?? false} />
        <Kpi icon={Heart} label="Likes" value={t.likes} tone={1} available={data?.capability?.likes ?? true} />
        <Kpi icon={MessageCircle} label="Comments" value={t.comments} tone={2} available={data?.capability?.comments ?? true} />
        <Kpi icon={Share2} label="Shares" value={t.shares} tone={3} available={data?.capability?.shares ?? true} />
        <Kpi icon={MousePointerClick} label="Clicks" value={t.clicks} tone={4} available={data?.capability?.clicks ?? false} />
      </div>

      <div className="mt-6 rounded-xl border border-border bg-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="font-semibold text-primary flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-accent" /> Trends
          </div>
          <div className="inline-flex rounded-lg border border-border overflow-hidden text-xs">
            {[30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d as 30 | 90)}
                className={`px-3 py-1.5 ${days === d ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-muted"}`}
              >
                Last {d} days
              </button>
            ))}
          </div>
        </div>
        {trend.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading trends…</p>
        ) : !trend.data?.hasData ? (
          <p className="text-sm text-muted-foreground">No provider metric snapshots are available for this period.</p>
        ) : (
          <TrendCharts data={trend.data?.series ?? []} />
        )}
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="font-semibold text-primary mb-3">By platform</div>
          {Object.keys(platformAgg).length === 0 ? (
            <p className="text-sm text-muted-foreground">No metrics logged yet — connect GHL and run Sync.</p>
          ) : (
            <ul className="space-y-3">
              {Object.entries(platformAgg)
                .sort((a: any, b: any) => b[1].impressions - a[1].impressions)
                .map(([platform, m]: any) => (
                  <li key={platform}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="capitalize font-medium text-primary">{platform}</span>
                      <span className="font-mono text-xs text-muted-foreground">
                        {m.posts} post{m.posts === 1 ? "" : "s"} · {m.impressions.toLocaleString()} impr
                      </span>
                    </div>
                    <div className="mt-1.5"><Bar value={m.impressions} max={maxImpressions} /></div>
                    <div className="mt-1 flex gap-3 text-xs text-muted-foreground">
                      <span>♥ {m.likes}</span>
                      <span>💬 {m.comments}</span>
                      <span>↗ {m.shares}</span>
                      <span>🔗 {m.clicks}</span>
                    </div>
                  </li>
                ))}
            </ul>
          )}
        </div>

        <div className="rounded-xl border border-border bg-card p-5">
          <div className="font-semibold text-primary mb-3 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-accent" /> Top performing posts
          </div>
          {topPosts.length === 0 ? (
            <p className="text-sm text-muted-foreground">No engagement data yet.</p>
          ) : (
            <ul className="space-y-3">
              {topPosts.map((p: any) => (
                <li key={p.id} className="border-b border-border last:border-0 pb-3 last:pb-0">
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                    <span className="capitalize font-medium text-primary">{p.platform}</span>
                    <span className="font-mono">score {p.score}</span>
                  </div>
                  <Bar value={p.score} max={maxScore} tone="bg-accent" />
                  <p className="mt-2 text-sm text-foreground line-clamp-2">{p.body}</p>
                  {p.link_url && (
                    <a href={p.link_url} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:underline">
                      <ExternalLink className="h-3 w-3" /> Open link
                    </a>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="font-semibold text-primary mb-3">Pipeline</div>
          <ul className="space-y-1.5 text-sm">
            {Object.entries(data?.byStatus ?? {}).map(([k, v]) => (
              <li key={k} className="flex justify-between items-center">
                <span className="capitalize text-muted-foreground">{k}</span>
                <span className="font-mono text-primary">{v as number}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="font-semibold text-primary mb-3">Volume by platform</div>
          <ul className="space-y-1.5 text-sm">
            {Object.entries(data?.byPlatform ?? {}).map(([k, v]) => (
              <li key={k} className="flex justify-between items-center">
                <span className="capitalize text-muted-foreground">{k}</span>
                <span className="font-mono text-primary">{v as number}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
